import React from "react";
import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { FaBell, FaInfoCircle } from "react-icons/fa";
import { Trans } from "@lingui/macro";

import Button from "../../components/Button";
import MinAmountInfo from "../../components/MinAmountInfo";
import { formatMoney } from "../../utils";
import NextImage from "next/image";
import NewIcon from "../../assets/new.png";
import PreviewAlertPriority from "../../assets/preview-alert-priority.jpg";

// Single toggle: priority applies to the donation's alert and mediashare alike.
const PriorityAlertSelector = ({ priorityAlert, currency, onToggle }) => {
  const { isFilled, isAvailable, isSelected, price, unmetMinimums } =
    priorityAlert;
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <FormControl mb={3} mt={4}>
      <Flex align="center" justify="space-between" gap="12px" mb={2}>
        <FormLabel mb={0}>
          <Trans>Tambahan:</Trans>
        </FormLabel>
        {/* same "Panduan" link as TopupGameDetail */}
        <Flex
          align="center"
          gap="6px"
          px="12px"
          py="6px"
          bg="whitish"
          borderRadius="6px"
          cursor="pointer"
          onClick={onOpen}
          _hover={{ bg: "gray.100" }}
          data-testid="priority-alert-info-link"
        >
          <Icon as={FaInfoCircle} w="16px" h="16px" color="#3182CE"/>
          <Text fontSize="14px" fontFamily="IBM Plex Mono" lineHeight="20px" color="#3182CE" fontWeight="500" textDecoration="underline">
            <Trans>Apa itu prioritas?</Trans>
          </Text>
        </Flex>
      </Flex>
      <Flex>
        <Button
          color="black"
          data-testid="priority-alert-button"
          type="button"
          mb={0}
          pressed={isSelected}
          backgroundColor="saweria-green.normal"
          paddingX="20px"
          paddingY="8px"
          borderRadius="6px"
          // design shadow; selected/disabled keep the Button's pressed look
          boxShadow={isSelected || !isAvailable ? "pressed" : "6px 6px 0px black"}
          filter={isAvailable ? "none" : "grayscale(75%)"}
          disabled={!isAvailable}
          onClick={() => {
            if (!isAvailable) return;
            onToggle();
          }}
          label={
            <Flex
              align="center"
              justify="space-between"
              gap="20px"
              fontFamily="IBM Plex Mono"
              fontSize="16px"
              lineHeight="24px"
              color="black"
            >
              {/* same NEW badge as the Tebak Gambar button (MediaSelector) */}
              <Flex align="center" gap={1}>
                <Text as="span" fontWeight={600} marginRight="10px">
                  <Trans>Prioritas</Trans>
                </Text>
                <NextImage src={NewIcon} alt="new" width="30" height="20" />
              </Flex>
              <Text as="span" fontWeight={400}>
                {`+${formatMoney(price, { currency })}`}
              </Text>
            </Flex>
          }
        />
      </Flex>
      {/* like "Min. muncul alert": only once an amount is filled in */}
      {isFilled && !isAvailable && (
        <MinAmountInfo
          labels={unmetMinimums.map((m) => m.label)}
          amounts={unmetMinimums.map((m) => m.amount)}
          currency={currency}
          reactIcon={FaBell}
        />
      )}
      <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader />
          <ModalCloseButton />
          <ModalBody>
            <VStack align="flex-start" spacing="20px" pb="20px" mt={2}>
              <Box w="100%">
                <NextImage
                  src={PreviewAlertPriority}
                  alt="Prioritas"
                  layout="responsive"
                />
              </Box>
              <Text fontSize="14px" fontFamily="IBM Plex Mono" lineHeight="20px">
                <Trans>
                  Overlaymu akan <b>muncul lebih awal</b> dibanding yang lain dan muncul
                  badge “Prioritas Bos”. Berlaku di semua overlay.
                </Trans>
              </Text>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </FormControl>
  );
};

export { PriorityAlertSelector };
